import React, { useEffect, useMemo, useState } from 'react';
import { auth, db } from '../services/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import type { SubjectPlan } from '../services/onboardingService';

type StageCode = 'NAO' | 'TEO' | 'EXE' | 'REV' | 'SIM';

interface ApprovalTopicRow {
  id: string;
  subject: string;
  topic: string;
  code: StageCode;
  questionCount: number;
  accuracy: number;
  updatedAt?: string;
}

interface ApprovalMapPageProps {
  onBack: () => void;
  onGoDashboard: () => void;
  onCreatePlan?: () => void;
  onViewAulas?: (subject: string, topic: string) => void;
  onboardingData: any;
}

const codeLabel: Record<StageCode, string> = {
  NAO: 'NÃO',
  TEO: 'TEORIA',
  EXE: 'EXERCÍCIOS',
  REV: 'REVISÃO',
  SIM: 'SIMULADO',
};

const codeBadge: Record<StageCode, string> = {
  NAO: 'bg-gray-100 text-gray-700',
  TEO: 'bg-blue-100 text-blue-700',
  EXE: 'bg-amber-100 text-amber-700',
  REV: 'bg-purple-100 text-purple-700',
  SIM: 'bg-emerald-100 text-emerald-700',
};

const stages: StageCode[] = ['NAO', 'TEO', 'EXE', 'REV', 'SIM'];

const normalize = (v: string) => (v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

const buildBaseRows = (onboardingData: any, persistedOnboardingData: any, planSubjects?: SubjectPlan[]): ApprovalTopicRow[] => {
  const fromOnboarding = Array.isArray(onboardingData?.extractedSubjects) ? onboardingData.extractedSubjects : [];
  const fromPersistedOnboarding = Array.isArray(persistedOnboardingData?.extractedSubjects) ? persistedOnboardingData.extractedSubjects : [];
  const sourceSubjects = fromOnboarding.length > 0
    ? fromOnboarding
    : (fromPersistedOnboarding.length > 0
      ? fromPersistedOnboarding
      : (Array.isArray(planSubjects) ? planSubjects : []));

  const examText = `${String(onboardingData?.targetExam || '')} ${String(persistedOnboardingData?.targetExam || '')}`.toLowerCase();
  const isBankExam = examText.includes('banco do brasil') || examText.includes('caixa econômica') || examText.includes('caixa economica') || /\bbb\b/.test(examText);

  const result: ApprovalTopicRow[] = [];

  sourceSubjects.forEach((s: any, subjectIdx: number) => {
    const rawSubject = String(s?.name || 'Matéria').trim() || 'Matéria';
    const normalizedSubject = normalize(rawSubject);
    const subject = (!isBankExam && normalizedSubject.includes('conhecimentos bancarios'))
      ? 'Conhecimentos Específicos'
      : rawSubject;
    const topics = Array.isArray(s?.topics) && s.topics.length > 0 ? s.topics : ['Tópico geral'];
    topics.forEach((t: string, idx: number) => {
      const topic = String(t || 'Tópico geral').trim() || 'Tópico geral';
      result.push({
        id: `${normalize(subject)}::${normalize(topic)}::${subjectIdx}-${idx}`,
        subject,
        topic,
        code: 'NAO',
        questionCount: 0,
        accuracy: 0,
      });
    });
  });

  if (result.length === 0) {
    result.push({
      id: 'fallback-1',
      subject: 'Plano Geral',
      topic: 'Organização inicial do edital',
      code: 'NAO',
      questionCount: 0,
      accuracy: 0,
    });
  }

  return result;
};

const ApprovalMapPage: React.FC<ApprovalMapPageProps> = ({ onBack, onGoDashboard, onCreatePlan, onViewAulas, onboardingData }) => {
  const [rows, setRows] = useState<ApprovalTopicRow[]>([]);
  const [missingStudyPlan, setMissingStudyPlan] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subjectFilter, setSubjectFilter] = useState('Todas');
  const [codeFilter, setCodeFilter] = useState<'TODOS' | StageCode>('TODOS');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkCode, setBulkCode] = useState<StageCode>('TEO');
  const [collapsedBySubject, setCollapsedBySubject] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const user = auth.currentUser;
        if (!user) {
          const guestRows = buildBaseRows(onboardingData, null);
          setRows(guestRows);
          setMissingStudyPlan(!guestRows.some((r) => r.id !== 'fallback-1'));
          return;
        }
        const ref = doc(db, 'users', user.uid);
        const snap = await getDoc(ref);
        const planSubjects = snap.exists() ? (snap.data()?.studyPlan?.subjects as SubjectPlan[] | undefined) : undefined;
        const persistedOnboardingData = snap.exists() ? snap.data()?.onboarding?.data : null;
        const baseRows = buildBaseRows(onboardingData, persistedOnboardingData, planSubjects);
        const saved = snap.exists() ? (snap.data()?.approvalMap?.topics || []) : [];
        const hasAnyRealSubject = baseRows.some((r) => r.id !== 'fallback-1');
        setMissingStudyPlan(!hasAnyRealSubject);

        if (!Array.isArray(saved) || saved.length === 0) {
          setRows(baseRows);
          return;
        }

        const map = new Map<string, any>();
        saved.forEach((r: any) => map.set(String(r.id), r));
        const merged = baseRows.map((r) => {
          const sv = map.get(r.id);
          if (!sv) return r;
          return {
            ...r,
            code: (sv.code || 'NAO') as StageCode,
            questionCount: Number(sv.questionCount || 0),
            accuracy: Number(sv.accuracy || 0),
            updatedAt: sv.updatedAt || undefined,
          };
        });
        setRows(merged);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [onboardingData]);

  const saveRows = async (nextRows: ApprovalTopicRow[]) => {
    const user = auth.currentUser;
    if (!user) return;
    setSaving(true);
    try {
      const ref = doc(db, 'users', user.uid);
      await setDoc(ref, {
        approvalMap: {
          topics: nextRows,
          updatedAt: serverTimestamp(),
        },
      }, { merge: true });
    } finally {
      setSaving(false);
    }
  };

  const updateCode = async (id: string, code: StageCode) => {
    const next = rows.map((r) => (r.id === id ? { ...r, code, updatedAt: new Date().toISOString() } : r));
    setRows(next);
    await saveRows(next);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const selectAllFiltered = () => {
    setSelectedIds(filtered.map((r) => r.id));
  };

  const clearSelection = () => setSelectedIds([]);

  const applyBulkCode = async () => {
    if (selectedIds.length === 0) return;
    const selectedSet = new Set(selectedIds);
    const next = rows.map((r) => selectedSet.has(r.id) ? { ...r, code: bulkCode, updatedAt: new Date().toISOString() } : r);
    setRows(next);
    await saveRows(next);
    setSelectedIds([]);
  };

  const subjects = useMemo(() => ['Todas', ...Array.from(new Set(rows.map((r) => r.subject)))], [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (subjectFilter !== 'Todas' && r.subject !== subjectFilter) return false;
      if (codeFilter !== 'TODOS' && r.code !== codeFilter) return false;
      if (search.trim()) {
        const q = normalize(search);
        if (!normalize(r.subject).includes(q) && !normalize(r.topic).includes(q)) return false;
      }
      return true;
    });
  }, [rows, subjectFilter, codeFilter, search]);

  const groupedFiltered = useMemo(() => {
    const grouped: Record<string, ApprovalTopicRow[]> = {};
    filtered.forEach((r) => {
      if (!grouped[r.subject]) grouped[r.subject] = [];
      grouped[r.subject].push(r);
    });
    return grouped;
  }, [filtered]);

  useEffect(() => {
    setCollapsedBySubject((prev) => {
      const next = { ...prev };
      Object.keys(groupedFiltered).forEach((subject) => {
        if (next[subject] === undefined) next[subject] = true;
      });
      return next;
    });
  }, [groupedFiltered]);

  const toggleSubject = (subject: string) => {
    setCollapsedBySubject((prev) => ({ ...prev, [subject]: !prev[subject] }));
  };

  const counters = useMemo(() => {
    const c: Record<StageCode, number> = { NAO: 0, TEO: 0, EXE: 0, REV: 0, SIM: 0 };
    rows.forEach((r) => c[r.code]++);
    return c;
  }, [rows]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 px-4 py-4 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          <button onClick={onBack} className="px-3 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold">← Voltar</button>
          <h1 className="text-lg md:text-2xl font-black text-gray-900">🗺️ MAPA DA APROVAÇÃO</h1>
          <button onClick={onGoDashboard} className="px-3 py-2 rounded-xl bg-emerald-100 text-emerald-700 font-bold">ÁREA DO ALUNO</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6 space-y-4">
        {missingStudyPlan && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-sm font-black text-amber-800">⚠️ Você ainda não tem plano de estudos gerado.</p>
              <p className="text-xs text-amber-700">Crie seu plano primeiro para preencher o Mapa da Aprovação com matérias e tópicos reais.</p>
            </div>
            {onCreatePlan && (
              <button onClick={onCreatePlan} className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm">
                Criar Plano de Estudos
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {stages.map((s) => (
            <div key={s} className={`rounded-xl px-3 py-3 text-center font-bold ${codeBadge[s]}`}>
              <p className="text-xs">{codeLabel[s]}</p>
              <p className="text-xl">{counters[s]}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-xs font-black text-gray-500 uppercase tracking-wide mb-2">Legenda dos códigos</p>
          <div className="flex flex-wrap gap-2 text-xs">
            {stages.map((s) => (
              <span key={s} className={`px-2 py-1 rounded-full font-bold ${codeBadge[s]}`}>
                {codeLabel[s]}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 mt-2">NÃO = ainda não iniciou · TEORIA = leitura/base · EXERCÍCIOS = prática · REVISÃO = reforço · SIMULADO = treino de prova.</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <select value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)} className="px-3 py-2 rounded-xl border border-gray-200">
            {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={codeFilter} onChange={(e) => setCodeFilter(e.target.value as any)} className="px-3 py-2 rounded-xl border border-gray-200">
            <option value="TODOS">Todos os códigos</option>
            {stages.map((s) => <option key={s} value={s}>{codeLabel[s]}</option>)}
          </select>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar tópico..." className="md:col-span-2 px-3 py-2 rounded-xl border border-gray-200" />
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={selectAllFiltered} className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-bold">Selecionar filtrados</button>
            <button onClick={clearSelection} className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-bold">Limpar seleção</button>
            <div className="text-xs text-gray-600 font-semibold md:ml-auto">{selectedIds.length} selecionado(s)</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
            <select value={bulkCode} onChange={(e) => setBulkCode(e.target.value as StageCode)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm">
              {stages.map((s) => <option key={s} value={s}>{codeLabel[s]}</option>)}
            </select>
            <button onClick={applyBulkCode} disabled={selectedIds.length === 0} className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-black disabled:opacity-50">Aplicar em lote</button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-6 text-gray-500">Carregando mapa...</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">Nenhum tópico encontrado com os filtros atuais.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {Object.entries(groupedFiltered).map(([subject, subjectRows]) => {
                const isCollapsed = collapsedBySubject[subject] ?? true;
                const progressed = subjectRows.filter((r) => r.code !== 'NAO').length;
                const progressPct = subjectRows.length > 0 ? Math.round((progressed / subjectRows.length) * 100) : 0;

                return (
                  <section key={subject} className="p-3 md:p-4 space-y-3">
                    <button
                      onClick={() => toggleSubject(subject)}
                      className="w-full flex items-center justify-between gap-2 bg-gray-50 hover:bg-gray-100 rounded-xl px-3 py-2 text-left"
                    >
                      <div className="min-w-0">
                        <p className="font-black text-gray-900 truncate">{subject}</p>
                        <p className="text-xs text-gray-500">{subjectRows.length} tópico(s) • {progressPct}% concluído</p>
                      </div>
                      <span className="text-gray-500 text-sm font-bold">{isCollapsed ? '▶' : '▼'}</span>
                    </button>

                    {!isCollapsed && (
                      <div className="space-y-2">
                        {subjectRows.map((r) => (
                          <div key={r.id} className="border border-gray-100 rounded-xl p-3 space-y-2">
                            <div className="flex items-start gap-3">
                              <input type="checkbox" className="mt-1" checked={selectedIds.includes(r.id)} onChange={() => toggleSelect(r.id)} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-gray-700 leading-relaxed">{r.topic}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {onViewAulas && (
                                  <button
                                    onClick={() => onViewAulas(r.subject, r.topic)}
                                    className="px-2 py-1.5 rounded-lg bg-cyan-50 text-cyan-700 border border-cyan-200 text-xs font-bold"
                                  >
                                    Ver aulas
                                  </button>
                                )}
                                <select value={r.code} onChange={(e) => updateCode(r.id, e.target.value as StageCode)} className="min-w-[130px] px-2 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold bg-white">
                                  {stages.map((s) => <option key={s} value={s}>{codeLabel[s]}</option>)}
                                </select>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div className="bg-gray-50 rounded-lg px-2 py-1.5">
                                <p className="text-gray-500">Questões</p>
                                <p className="font-bold text-gray-800">{r.questionCount}</p>
                              </div>
                              <div className="bg-gray-50 rounded-lg px-2 py-1.5">
                                <p className="text-gray-500">Acerto</p>
                                <p className="font-bold text-gray-800">{r.accuracy}%</p>
                              </div>
                              <div className="bg-gray-50 rounded-lg px-2 py-1.5">
                                <p className="text-gray-500">Atualizado</p>
                                <p className="font-bold text-gray-800">{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString('pt-BR') : '—'}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500">{saving ? 'Salvando alterações...' : 'Alterações salvas automaticamente.'}</p>
      </main>
    </div>
  );
};

export default ApprovalMapPage;
